/**
 * チェスの駒を SVG で描画する。
 *
 * 以前は Unicode の filled 駒 (♚♛♜♝♞♟) をテキストで描いていたが、 実体はシステムフォント
 * なので macOS / Windows / Android で別の駒が出ていた。 ここで自前のベクタに固定する。
 *
 * - identity は**側面のシルエット**にある (真上から見たチェスの駒はただの円)。
 *   なので他ゲームのような真上視点のマテリアル化はしない。 記号を正とする。
 *   → docs/design/MATERIAL_RENEWAL.md §3.8
 * - viewBox は 0 0 100 100、 接地は y=91。 ナイト以外は左右対称。
 * - ミニ盤 (22px 相当) で King / Queen / Bishop が分かれることを条件に形を決めてある。
 */
import type { PieceKind, Side } from '../../games/chess';

/** 全駒で共通の台座。 二段の縁を持たせて挽き物に見せる。 */
const BASE = 'M 21 91 L 79 91 L 79 86 Q 79 83 75 82 L 25 82 Q 21 83 21 86 Z';
/** ナイトは首が細いので台座もやや狭い。 */
const BASE_N = 'M 23 91 L 77 91 L 77 86 Q 77 83 73 82 L 27 82 Q 23 83 23 86 Z';

interface Shape {
  /** 塗り + 輪郭。 駒の本体。 */
  main: string[];
  /** 輪郭のみ。 黒駒では明色、 白駒では暗色で入る彫り線。 */
  inner?: string[];
}

const SHAPES: Record<PieceKind, Shape> = {
  // 球の頭 + 襟 + 裾広がり
  Pawn: {
    main: [
      'M 50 19 A 11.5 11.5 0 1 1 49.9 19 Z',
      'M 40 40 L 60 40 Q 62 40 62 43 Q 62 46 60 46 L 40 46 Q 38 46 38 43 Q 38 40 40 40 Z',
      'M 43 46 Q 43 60 36 70 Q 32 76 30 82 L 70 82 Q 68 76 64 70 Q 57 60 57 46 Z',
      BASE,
    ],
  },

  // 狭間 3 つ + 括れ + 裾
  Rook: {
    main: [
      'M 27 16 L 27 33 L 73 33 L 73 16 L 64 16 L 64 23 L 56 23 L 56 16 L 44 16 L 44 23 L 36 23 L 36 16 Z',
      'M 31 33 L 69 33 L 69 39 Q 64 42 64 46 L 64 64 Q 64 68 69 71 L 69 77 L 31 77 L 31 71 Q 36 68 36 64 L 36 46 Q 36 42 31 39 Z',
      'M 28 77 L 72 77 L 72 82 L 28 82 Z',
      BASE,
    ],
    inner: ['M 36 46 L 64 46 M 36 64 L 64 64'],
  },

  // 頂の玉 + 司教冠 + 切れ込み
  Bishop: {
    main: [
      'M 50 9 A 5.5 5.5 0 1 1 49.9 9 Z',
      'M 50 18 Q 33 29 33 45 Q 33 57 50 63 Q 67 57 67 45 Q 67 29 50 18 Z',
      'M 37 63 L 63 63 Q 67 64 67 68 L 33 68 Q 33 64 37 63 Z',
      'M 40 68 Q 40 76 33 82 L 67 82 Q 60 76 60 68 Z',
      BASE,
    ],
    inner: ['M 54 26 Q 46 34 50 46'],
  },

  // 左向きの馬首。 顎先が左へ出て鼻筋が通ることで馬に見える
  Knight: {
    main: [
      'M 28 82 ' +
        'C 28 69 31 59 39 53 ' + // 喉から顎へ
        'C 35 55 30 57 25 54 ' + // 下唇・顎先が左へ出る
        'C 20 51 21 45 26 41 ' + // 鼻面の先端から鼻筋
        'C 32 36 37 31 40 25 ' + // 額へ
        'L 43 13 L 50 26 ' + // 耳 (前)
        'L 57 12 L 62 28 ' + // 耳 (後)
        'C 70 32 75 42 76 54 ' + // 頭頂から鬣
        'C 77 66 76 75 74 82 Z', // 首の背から台座へ
      BASE_N,
    ],
    inner: [
      'M 27 49 Q 32 48 36 50',
      'M 43 33 A 2.5 2.5 0 1 1 42.9 33 Z',
      'M 55 30 Q 64 38 66 50 Q 67 62 66 74',
    ],
  },

  // 5 つの尖りと玉 + 幅広の襟
  Queen: {
    main: [
      'M 27 15 A 4.6 4.6 0 1 1 26.9 15 Z',
      'M 38.5 10 A 4.6 4.6 0 1 1 38.4 10 Z',
      'M 50 7.5 A 5.0 5.0 0 1 1 49.9 7.5 Z',
      'M 61.5 10 A 4.6 4.6 0 1 1 61.4 10 Z',
      'M 73 15 A 4.6 4.6 0 1 1 72.9 15 Z',
      'M 27 20 L 34 46 L 66 46 L 73 20 L 63 15 L 56 40 L 50 13 L 44 40 L 37 15 Z',
      'M 34 46 L 66 46 Q 70 48 70 52 L 30 52 Q 30 48 34 46 Z',
      'M 33 52 Q 34 68 28 82 L 72 82 Q 66 68 67 52 Z',
      BASE,
    ],
    inner: ['M 36 60 L 64 60 M 34 70 L 66 70'],
  },

  // 十字 + 冠 + 胴
  King: {
    main: [
      'M 46 6 L 54 6 L 54 12 L 60 12 L 60 20 L 54 20 L 54 27 L 46 27 L 46 20 L 40 20 L 40 12 L 46 12 Z',
      'M 30 30 Q 40 24 50 27 Q 60 24 70 30 L 68 50 L 32 50 Z',
      'M 32 50 L 68 50 Q 72 52 72 56 L 28 56 Q 28 52 32 50 Z',
      'M 32 56 Q 33 70 27 82 L 73 82 Q 67 70 68 56 Z',
      BASE,
    ],
    inner: ['M 36 64 L 64 64 M 34 73 L 66 73'],
  },
};

interface Props {
  kind: PieceKind;
  owner: Side;
  /** インライン (昇格ダイアログ等) で使うときの一辺 px。 省略時は親に合わせて伸縮する。 */
  size?: number;
}

export function ChessPieceGlyph({ kind, owner, size }: Props) {
  const white = owner === 'First';
  const shape = SHAPES[kind];
  // 黄楊のクリームと黒檀の黒。
  // 黒駒の輪郭は**明色**にする。 near-black の輪郭だと暗升の上でコントラストが
  // 1.5:1 しか出ず (図形の目安は 3:1)、 駒の形が沈む。 明色にすると暗升では輪郭が
  // 形を出し、 明升では黒い塗り自体が効くので輪郭は目立たない。
  // 磨いた黒檀は縁に光を拾うので、 見た目としても嘘ではない。
  const fill = white ? '#f4ead6' : '#23201d';
  const edge = white ? '#211a12' : '#ece3d2';
  const ink = white ? '#211a12' : '#cfc3ad';
  const sized = size != null ? { width: size, height: size } : undefined;

  return (
    <svg
      className="chess-glyph"
      /* 図柄は x 18.5-81.5 / y 4.5-92.5 (線幅 3 の外側込み) に収まる。
         余白の大きい viewBox だとレターボックスで駒が小さく見えるので、
         図柄の外接矩形に寄せた正方で切る。 */
      viewBox="4 3 92 92"
      style={sized}
      aria-hidden
      focusable="false"
    >
      {shape.main.map((d) => (
        <path
          key={d}
          d={d}
          fill={fill}
          stroke={edge}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {shape.inner?.map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={ink}
          strokeWidth={2.4}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}
